import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Truck } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '../../components/layout';
import { type ScannedLine, ScanInput, addScan } from '../../components/scan-input';
import { Alert } from '../../components/ui/alert';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Dialog } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { API_BASE, api, errorMessage } from '../../lib/api';
import type { PendingShipment, PickOrder } from '../../lib/types';
import { formatDateTime } from '../../lib/utils';
import { LocationSelect } from './stock';

export function FulfillmentPage() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [locationId, setLocationId] = useState('');
  const [packing, setPacking] = useState<PickOrder>();
  const [error, setError] = useState<string>();
  const [busyId, setBusyId] = useState<string>();
  const [tracking, setTracking] = useState<Record<string, string>>({});
  const suffix = locationId ? `?locationId=${locationId}` : '';
  const queue = useQuery({
    queryKey: ['warehouse', 'pick-queue', locationId],
    queryFn: () => api<{ items: PickOrder[] }>(`/warehouse/pick-queue${suffix}`),
  });
  const shipments = useQuery({
    queryKey: ['warehouse', 'shipments', locationId],
    queryFn: () => api<{ items: PendingShipment[] }>(`/warehouse/shipments${suffix}`),
  });

  const run = async (id: string, action: () => Promise<unknown>) => {
    setBusyId(id);
    setError(undefined);
    try {
      await action();
      await queryClient.invalidateQueries({ queryKey: ['warehouse'] });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusyId(undefined);
    }
  };

  return (
    <>
      <PageHeader title={t('wh.fulfillmentTitle')} description={t('wh.fulfillmentHint')} />
      <div className="mb-4">
        <LocationSelect value={locationId} onChange={setLocationId} />
      </div>
      {error ? (
        <Alert variant="destructive" className="mb-4">
          {error}
        </Alert>
      ) : null}

      <h2 className="mb-3 text-lg font-semibold">
        {t('wh.toPack')} ({queue.data?.items.length ?? 0})
      </h2>
      {queue.data?.items.length === 0 ? (
        <p className="mb-6 text-sm text-muted-foreground">{t('wh.nothingToPack')}</p>
      ) : null}
      <div className="mb-8 grid gap-3 md:grid-cols-2">
        {(queue.data?.items ?? []).map((o) => (
          <Card key={`${o.orderId}:${o.locationId}`} className="p-4">
            <div className="mb-2 flex items-center justify-between gap-2">
              <span className="font-semibold">{o.number}</span>
              <span className="flex gap-1">
                <Badge variant="secondary">{o.locationCode}</Badge>
                <Badge variant={o.status === 'PROCESSING' ? 'warning' : 'outline'}>
                  {o.status}
                </Badge>
              </span>
            </div>
            <p className="mb-2 text-xs text-muted-foreground">
              {[o.carrier, o.shippingMethodCode, o.pickupPointId ? `#${o.pickupPointId}` : null]
                .filter(Boolean)
                .join(' · ')}
              {o.placedAt ? ` · ${formatDateTime(o.placedAt, i18n.language)}` : ''}
            </p>
            <ul className="mb-3 text-sm">
              {o.lines.map((l) => (
                <li key={l.orderItemId}>
                  <span className="font-mono">{l.sku}</span> × {l.qty} — {l.name}
                </li>
              ))}
            </ul>
            <div className="flex gap-2">
              {o.status === 'RESERVED' ? (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busyId === o.orderId}
                  onClick={() =>
                    void run(o.orderId, () =>
                      api(`/warehouse/orders/${o.orderId}/pick`, { method: 'POST' }),
                    )
                  }
                >
                  {t('wh.startPicking')}
                </Button>
              ) : null}
              <Button
                size="sm"
                onClick={() => {
                  setPacking(o);
                }}
              >
                {t('wh.pack')}
              </Button>
            </div>
          </Card>
        ))}
      </div>

      <h2 className="mb-3 text-lg font-semibold">
        {t('wh.toShip')} ({shipments.data?.items.length ?? 0})
      </h2>
      <div className="grid gap-2">
        {(shipments.data?.items ?? []).map((s) => (
          <Card key={s.id} className="flex flex-wrap items-center gap-3 p-3 text-sm">
            <span className="font-semibold">{s.orderNumber}</span>
            <Badge variant="secondary">{s.locationCode}</Badge>
            <span>
              {s.carrier} · {s.service}
            </span>
            {s.trackingNumber ? <span className="font-mono">{s.trackingNumber}</span> : null}
            <span className="ml-auto flex items-center gap-2">
              {s.hasLabel ? (
                <a
                  className="inline-flex items-center gap-1 text-primary hover:underline"
                  href={`${API_BASE}/warehouse/shipments/${s.id}/label.pdf`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <FileText className="size-4" />
                  {t('wh.printLabel')}
                </a>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busyId === s.id}
                  onClick={() =>
                    void run(s.id, () =>
                      api(`/warehouse/shipments/${s.id}/label`, { method: 'POST' }),
                    )
                  }
                >
                  {t('wh.createLabel')}
                </Button>
              )}
              {!s.trackingNumber ? (
                <Input
                  aria-label={t('po.tracking')}
                  placeholder={t('po.tracking')}
                  value={tracking[s.id] ?? ''}
                  onChange={(e) => {
                    setTracking((v) => ({ ...v, [s.id]: e.target.value }));
                  }}
                  className="h-8 w-44"
                />
              ) : null}
              <Button
                size="sm"
                disabled={busyId === s.id || (!s.trackingNumber && !tracking[s.id]?.trim())}
                onClick={() =>
                  void run(s.id, () =>
                    api(`/warehouse/shipments/${s.id}/ship`, {
                      method: 'POST',
                      body: s.trackingNumber ? {} : { trackingNumber: tracking[s.id]?.trim() },
                    }),
                  )
                }
              >
                <Truck />
                {t('wh.handOver')}
              </Button>
            </span>
          </Card>
        ))}
      </div>

      {packing ? (
        <PackDialog
          order={packing}
          onClose={() => {
            setPacking(undefined);
          }}
        />
      ) : null}
    </>
  );
}

function PackDialog({ order, onClose }: { order: PickOrder; onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [scanned, setScanned] = useState<ScannedLine[]>([]);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const countFor = (sku: string, gtin: string | null) =>
    scanned.filter((s) => s.code === sku || s.code === gtin).reduce((n, s) => n + s.qty, 0);
  const extra = scanned.filter(
    (s) => !order.lines.some((l) => l.sku === s.code || l.gtin === s.code),
  );
  const complete =
    extra.length === 0 && order.lines.every((l) => countFor(l.sku, l.gtin) === l.qty);

  const submit = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await api(`/warehouse/orders/${order.orderId}/pack`, {
        method: 'POST',
        body: { locationId: order.locationId, scanned },
      });
      await queryClient.invalidateQueries({ queryKey: ['warehouse'] });
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={`${t('wh.pack')} ${order.number}`}
      description={t('wh.packHint')}
      footer={
        <>
          <Button
            variant="outline"
            onClick={() => {
              setScanned([]);
            }}
          >
            {t('wh.reset')}
          </Button>
          <Button disabled={busy || !complete} onClick={() => void submit()}>
            {t('wh.packed')}
          </Button>
        </>
      }
    >
      <div className="grid gap-4 text-sm">
        <ScanInput
          onScan={(code) => {
            setScanned((lines) => addScan(lines, code));
          }}
        />
        <table className="w-full">
          <thead className="text-left text-muted-foreground">
            <tr>
              <th className="py-1">{t('wh.sku')}</th>
              <th>{t('wh.item')}</th>
              <th className="text-right">{t('wh.expected')}</th>
              <th className="text-right">{t('wh.scanned')}</th>
            </tr>
          </thead>
          <tbody>
            {order.lines.map((l) => {
              const got = countFor(l.sku, l.gtin);
              return (
                <tr key={l.orderItemId} className="border-t">
                  <td className="py-1 font-mono">{l.sku}</td>
                  <td>{l.name}</td>
                  <td className="text-right">{l.qty}</td>
                  <td
                    className={
                      got === l.qty
                        ? 'text-right text-emerald-700'
                        : got > l.qty
                          ? 'text-right text-destructive'
                          : 'text-right'
                    }
                  >
                    {got}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {extra.map((s) => (
          <Alert key={s.code} variant="destructive">
            {t('wh.notInOrder', { code: s.code })}
          </Alert>
        ))}
        {error ? <Alert variant="destructive">{error}</Alert> : null}
      </div>
    </Dialog>
  );
}
